import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isSmallScreen } from '../../../shared/utils/isSmallScreen';
import { SearchBarPanel } from '../../../shared/components/SearchBarPanel/SearchBarPanel';
import { SearchPanelHeader } from '../../../shared/components/SearchBarPanel/SearchPanelHeader';
import {
  SearchBarControls,
  SearchOptions,
} from '../../../shared/components/SearchBarPanel/SearchBarControls';
import { DepartureField } from '../DepartureField';
import { DateField } from '../DateField';
import { TripLengthField } from '../TripLengthField';

const isOtherActive = (active, pane) => {
  return active !== pane && active !== null;
};

export function SearchBar(props) {
  const {
    id,
    isOpen,
    searchBarLabel,
    texts,
    activePane,
    onOptionClick,
    onClosePane,
    onOpenSearchClick,
    onSearchClick,

    selectedDepartures,
    departures,
    onDepartureChange,

    availableDates,

    selectedFromDate,
    onFromDateChange,

    selectedToDate,
    onToDateChange,

    selectedTripLength,
    tripLengths,
    onTripLengthChange,
  } = props;

  const ctrlsRef = useRef();

  useEffect(() => {
    const handleClick = (event) => {
      if (ctrlsRef.current && !ctrlsRef.current.contains(event.target)) {
        // outside click
        onClosePane();
      }
    };

    if (activePane && !isSmallScreen()) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [activePane, ctrlsRef.current]);

  const departureField = (
    <DepartureField
      header={texts.departureLabel}
      selected={selectedDepartures}
      departures={departures}
      isOpen={activePane === SearchOptions.departure}
      isOtherActive={isOtherActive(activePane, SearchOptions.departure)}
      onClose={onClosePane}
      onChange={onDepartureChange}
      texts={texts}
    />
  );

  const dateFromField = (
    <DateField
      availableDates={availableDates}
      isOpen={activePane === SearchOptions.dateFrom}
      isOtherActive={isOtherActive(activePane, SearchOptions.dateFrom)}
      onClose={onClosePane}
      onChange={onFromDateChange}
      header={texts.datePicker}
      overlayTitle={texts.datePicker}
      submitLabel={texts.datePickerSelect}
      selected={selectedFromDate}
    />
  );

  const dateToField = (
    <DateField
      availableDates={availableDates}
      isOpen={activePane === SearchOptions.dateTo}
      isOtherActive={isOtherActive(activePane, SearchOptions.dateTo)}
      onClose={onClosePane}
      onChange={onToDateChange}
      header={texts.datePickerTo}
      overlayTitle={texts.datePickerTo}
      submitLabel={texts.datePickerSelect}
      selected={selectedToDate}
      dateFrom={selectedFromDate}
    />
  );

  const tripLengthField = (
    <TripLengthField
      isOpen={activePane === SearchOptions.tripLength}
      isOtherActive={isOtherActive(activePane, SearchOptions.tripLength)}
      onClose={onClosePane}
      header={texts.searchLengthHeader}
      overlayTitle={texts.searchLengthHeader}
      tripLengthsTitle={texts.flightPlusHotel}
      selected={selectedTripLength}
      tripLengths={tripLengths}
      onChange={onTripLengthChange}
    />
  );

  return (
    <SearchBarPanel id={id}>
      <SearchPanelHeader isHidden={isOpen} buttonLabel={texts.change} onClick={onOpenSearchClick}>
        {searchBarLabel}
      </SearchPanelHeader>
      <SearchBarControls
        isOpen={isOpen}
        ref={ctrlsRef}
        buttonLabel={texts.search}
        departureField={departureField}
        dateFromField={dateFromField}
        dateToField={dateToField}
        tripLengthField={tripLengthField}
        onSearch={onSearchClick}
        onOpenPane={onOptionClick}
      />
    </SearchBarPanel>
  );
}

SearchBar.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool,
  activePane: PropTypes.string,
  searchBarLabel: PropTypes.node,
  texts: PropTypes.object,
  onOpenSearchClick: PropTypes.func,
  onSearchClick: PropTypes.func,
  onOptionClick: PropTypes.func,
  onClosePane: PropTypes.func,
  selectedDepartures: PropTypes.array,
  departures: PropTypes.array,
  availableDates: PropTypes.array,
  selectedFromDate: PropTypes.object,
  selectedToDate: PropTypes.object,
  selectedTripLength: PropTypes.object,
  tripLengths: PropTypes.array,
  onDepartureChange: PropTypes.func,
  onTripLengthChange: PropTypes.func,
  onFromDateChange: PropTypes.func,
  onToDateChange: PropTypes.func,
};
